<template>
  <div :data-test-id="dataTestId">
    <!-- Multiple PromoCardImages - Size will be Medium (for 2) or Small (for 3) -->
    <div class="flex flex-col gap-4">
      <template v-if="fields.heading">
        <div class="max-w-full lg:max-w-[936px] w-full lg:w-[936px] text-racq-dark-grey text-base-bold">
          <RichText :field="fields.heading" />
        </div>
      </template>
      <div class="flex flex-col md:flex-row md:flex-wrap gap-4 w-full">
        <div
          v-for="(promoCardImage, index) in promoCardImages"
          :key="index"
          :data-test-id="promoCardImage.fields.dataTestId?.value || dataTestId + '_' + index"
        >
          <template v-if="promoCardImage.fields.isChevron.value && promoCardImage.fields.chevronUrl">
            <Link
              :field="promoCardImage.fields.chevronUrl"
              class="group block no-underline active:no-underline shadow-md hover:shadow-xl rounded-lg active:rounded-lg active:!shadow-none"
            >
              <PromoCardImageContent
                :promoCardImage="promoCardImage"
                :isWrappedInLink="true"
                :tagSize="TagSize"
                :tagBgColour="TagBgColour"
                :tagTypes="TagTypes"
                :getMaxImageWidthClasses="getMaxImageWidthClasses"
                :getBackgroundColourClass="getBackgroundColourClass"
                :isValidPrimaryButton="isValidPrimaryButton"
                :isValidSecondaryButton="isValidSecondaryButton"
              />
            </Link>
          </template>
          <template v-else>
            <PromoCardImageContent
              :class="{
                'shadow-md': promoCardImage.fields.showBorder,
                'hover:shadow-lg': promoCardImage.fields.showBorder,
              }"
              :promoCardImage="promoCardImage"
              :isWrappedInLink="false"
              :tagSize="TagSize"
              :tagBgColour="TagBgColour"
              :tagTypes="TagTypes"
              :getMaxImageWidthClasses="getMaxImageWidthClasses"
              :getBackgroundColourClass="getBackgroundColourClass"
              :isValidPrimaryButton="isValidPrimaryButton"
              :isValidSecondaryButton="isValidSecondaryButton"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Link, ComponentFields, Image, ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import { BackgroundColours } from '@/interfaces/forms/background-colour.interface';
import { IPromoCardImageItem } from '@/interfaces/entities/promo-card-image.interface';
import { TagBgColour, TagSize, TagTypes } from '@/enums/tag-enums';
import PromoCardImageContent from '@/components/PromoCardImage/PromoCardImageContent.vue';
import { usePageStore } from '@/store';

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = defineProps<{
  fields: ComponentFields;
  rendering?: ComponentRendering;
}>();

const promoCardImages = computed(() => props.fields.items as IPromoCardImageItem[]);
const firstPromoCardImage = computed(() => promoCardImages.value[0] as IPromoCardImageItem);
const renderChevronSingle = computed(
  () => firstPromoCardImage.value.fields.isChevron.value && firstPromoCardImage.value.fields.chevronUrl
);

const dataTestId = usePageStore().dataTestId(props.rendering ?? firstPromoCardImage.value);

function isValidPrimaryButton(promoCardImage: IPromoCardImageItem) {
  if (promoCardImage.fields.primaryButton?.value.text && promoCardImage.fields.primaryButton) {
    return true;
  }
  return false;
}

function isValidSecondaryButton(promoCardImage: IPromoCardImageItem) {
  if (promoCardImage.fields.secondaryButton?.value.text && promoCardImage.fields.secondaryButton) {
    return true;
  }
  return false;
}

function getMaxImageWidthClasses() {
  const promoCards = props.fields.items as IPromoCardImageItem[];
  if (promoCards.length === 1) {
    // return lg / default
    return `w-[288px] ${props.fields.height}`;
  } else if (promoCards.length === 2) {
    // return md
    return 'w-[300px] md:w-[340px]';
  } else {
    // return sm
    return 'md:w-[288px]';
  }
}

function getBackgroundColourClass(backgroundColour: any) {
  if (!backgroundColour?.value) {
    return '';
  }
  switch (backgroundColour.value) {
    case BackgroundColours.WHITE:
      return 'bg-racq-true-white';
    case BackgroundColours.LIGHTGREY:
      return 'bg-racq-neutral-grey';
    case BackgroundColours.GREY:
      return 'bg-racq-anti-flash-white';
    default:
      return '';
  }
}
</script>
